import React from 'react';
import {
  getSystem,
  TypeImplementation,
  ExpressionPath,
  Expressions,
  matchExpression,
} from '@packages/systems/wfdl';
import {
  ExpressionEditor,
  updateExpressionAction,
  useExpressionEditorImplementationContext,
} from '@packages/systems/ExpressionEditor-utils';
import {useTheme} from '@packages/utilities/styled';
import {rightSidebarLabelWidth} from '@packages/systems/design-system/constants/DesignerUI';

import {
  TBoolean,
  EBoolean,
  ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {Checkbox, WarningTriangleIcon} from '@packages/systems/spring';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';
import {selectSelectedElement} from '@packages/utilities/platform-selectors';
import {IMAGE_WIDTH_CHANGED} from '@packages/systems/designer/actions';
import {get, getString} from '@packages/utilities/json';
import {isDynamoImgBinding} from '@packages/systems/dynamo/wfdl';
import {isBoundElement} from '@packages/systems/dynamo/utils/DynamoElementUtils';

import {
  ContextualExpressions,
  stripAnn,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {
  useDesignerSelector,
  useDispatch,
} from '@packages/systems/designer/flux';

const imageWidthPath: ExpressionPath = [
  {in: 'Record', at: 'attr'},
  {in: 'Record', at: 'width'},
];

const IMAGE_PLACEHOLDER_ID = 'plugins/Basic/assets/placeholder.svg';

const getImageId = (element: ExpressionElement | null): string | undefined => {
  const imageIdMatcher = matchExpression({
    Record: (data) => {
      return data.img ? Expressions.getLiteral(data.img)?.value : null;
    },
    _: () => null,
  });
  if (!element) return undefined;
  const literal = imageIdMatcher(Expressions.getElementData(element));
  if (!literal) return undefined;
  const jsonValue = get(literal, ['id']);
  if (!jsonValue) return undefined;
  return getString(jsonValue);
};

const getAssetWidth = (
  selectedElement: ExpressionElement | null,
  env: TypeImplementation.DataTypeEnvironment
): number => {
  const imageId = getImageId(selectedElement);
  const asset = imageId
    ? env.asset.get({type: 'image', id: imageId})
    : env.asset.get({type: 'image', id: IMAGE_PLACEHOLDER_ID});
  return asset && asset.width ? asset.width : 0;
};

const ImageReversedBooleans = TypeImplementation.Alias({
  type: TBoolean,
  render: function ImageReversedBooleanRender({expr, dispatch}) {
    const {type} = getAnn(expr);

    const system = type ? getSystem(type) : null;

    const exprValue = ContextualExpressions.getBoolean(stripAnn(expr));

    const selectedElement = useDesignerSelector(selectSelectedElement);
    const {env} = useExpressionEditorImplementationContext();
    const assetWidth = getAssetWidth(selectedElement, env);
    const currentWidth = selectedElement
      ? Expressions.getElementDataAt(
          selectedElement,
          imageWidthPath,
          'Text',
          undefined
        )
      : undefined;
    const hiDPI = currentWidth ? Number(currentWidth) <= assetWidth / 2 : false;

    const toggle = () => {
      dispatch(updateExpressionAction(EBoolean(!exprValue || false)));
    };

    const fluxDispatch = useDispatch();

    const toggleHDPI = () => {
      fluxDispatch({
        type: IMAGE_WIDTH_CHANGED,
        payload: {
          width: hiDPI ? 'auto' : (assetWidth / 2).toString(),
        },
      });
    };

    const {gutters} = useTheme();
    const isBound = selectedElement
      ? isDynamoImgBinding(selectedElement.data) ||
        isBoundElement(selectedElement, 'src')
      : false;

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${rightSidebarLabelWidth}px minmax(0, auto)`,
          gridGap: gutters.x8,
          alignItems: 'center',
        }}
      >
        {!isBound ? (
          <div
            data-automation-id="ee-image-hidpi-checkbox-wrapper"
            style={{
              height: '18px',
              gridColumn: '2 / -1',
              display: 'flex',
              gap: gutters.x8,
              alignItems: 'center',
            }}
          >
            <Checkbox
              data-automation-id="ee-image-input-hidpi-checkbox"
              label="HiDPI image"
              isChecked={hiDPI}
              onChange={toggleHDPI}
            />
          </div>
        ) : null}
        <div
          style={{
            height: '18px',
            gridColumn: '2 / -1',
            display: 'flex',
            gap: gutters.x8,
            alignItems: 'center',
          }}
        >
          <ExpressionEditor expression={expr} dispatch={dispatch} />
          <span
            onClick={toggle}
            onKeyDown={toggle}
            role="checkbox"
            aria-checked={exprValue || false}
            tabIndex={0}
            style={{lineHeight: 1}}
          >
            {(system && system.label) || ''}
          </span>
          {exprValue ? (
            <TooltipTrigger
              alternate
              content="This setting forces the browser to use the original image size. This may increase site load times."
            >
              <WarningTriangleIcon />
            </TooltipTrigger>
          ) : null}
        </div>
      </div>
    );
  },
});

export default ImageReversedBooleans;
